<div class="container my-4" *transloco="let t">
  @if (failure(); as failed) {
    <div class="alert alert-warning" role="alert">{{ t(failed.translationKey) }}</div>
  } @else if (loading() && !input()) {
    <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
  } @else if (input(); as current) {
    @if (!delivery()) {
      <div class="alert alert-info" role="alert">
        {{ t('inputs.not_yours_yet') }}
        <a class="ms-1" [routerLink]="['/contributions/inputs', current.slug]">{{ t('inputs.see_the_input') }}</a>
      </div>
    } @else if (delivery(); as mine) {
      <div class="row g-4">
        <div class="col-lg-8">
          <p class="mb-3">
            <a [routerLink]="['/contributions/inputs', current.slug]">{{ t('inputs.see_the_input') }}</a>
            @if (current.ideas.length) {
              <span class="text-muted ms-2">{{ t('inputs.helps_with') }}:</span>
              @for (idea of current.ideas; track idea.slug; let last = $last) {
                <a class="ms-1" [routerLink]="['/ideas', idea.slug]">{{ idea.name }}</a>@if (!last) {,}
              }
            }
          </p>

          <!-- The question handed out when they took the input: what the video answers. -->
          @if (delivery()?.subject; as subject) {
            <section class="video-question p-4 mb-4">
              <h2 class="h6 text-uppercase text-muted mb-2">{{ t('inputs.video.your_question') }}</h2>
              <p class="lead fs-4 mb-2">{{ subject.question || subject.title }}</p>
              <div class="d-flex flex-wrap align-items-center text-muted">
                @if (delivery()?.culture; as culture) {
                  <multisite-avatar-pill class="me-2" [compact]="true" [clickable]="false" [cursorNone]="true" [active]="false"
                    [imageUrl]="flagUrl(culture.flag)" [text]="culture.name ?? ''" [fallbackTranslationKey]="'tracking.missing'"></multisite-avatar-pill>
                }
                @if (subject.question && subject.title) {
                  <span class="small">{{ subject.title }}</span>
                }
              </div>
            </section>
          }

          @if (current.images.hero; as hero) {
            <figure class="mb-4">
              <img class="img-fluid rounded border" [src]="hero | imageUrl: heroTransform" [alt]="hero.altText ?? ''" />
            </figure>
          }

          @if (current.instructions.videos.length) {
            <h2 class="h5 mt-4">{{ t('inputs.videos') }}</h2>
            <ul>
              @for (video of current.instructions.videos; track $index) {
                <li>
                  @if (video.pageUrl) {
                    <a [href]="video.pageUrl" target="_blank" rel="noopener">{{ video.name || video.pageUrl }}</a>
                  } @else {
                    {{ video.name }}
                  }
                </li>
              }
            </ul>
          }

          @if (current.instructions.particular.length) {
            <h2 class="h5 mt-4">{{ t('inputs.instructions_particular') }}</h2>
            @for (instruction of current.instructions.particular; track $index) {
              @if (instruction.s) {
                <p class="fw-semibold mb-1">{{ instruction.s }}</p>
              }
              @if (instruction.m) {
                <div [innerHTML]="instruction.m | insightMarkdown"></div>
              }
            }
          }

          <!-- The task's tips for recording: its long description, Markdown with its own heading. -->
          @if (current.task.longDescription || current.task.description; as taskText) {
            <div class="mt-4 mb-4" [innerHTML]="taskText | insightMarkdown"></div>
          }

          <!-- The task's guidance texts, as staff add them to the task — none yet for video. -->
          @if (current.instructions.general.length) {
            <h2 class="h5 mt-4">{{ t('inputs.video.guidance') }}</h2>
            <div class="list-group mb-3">
              @for (instruction of current.instructions.general; track $index) {
                <div class="list-group-item">
                  @if (instruction.s) {
                    <p class="fw-semibold mb-1">{{ instruction.s }}</p>
                  }
                  @if (instruction.m) {
                    <div class="small" [innerHTML]="instruction.m | insightMarkdown"></div>
                  }
                </div>
              }
            </div>
          }

          <div class="mt-4">
            <multisite-delivery-conversation
              [deliveryId]="mine.id"
              titleTranslationKey="inputs.video.conversation"
              descriptionTranslationKey="inputs.video.conversation_help"
              translationScope="inputs">
            </multisite-delivery-conversation>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="video-send-sticky">
            <!-- How to send it: the one instruction that matters most, so it stands apart. -->
            <section class="card video-send mb-3" aria-labelledby="video-send-title">
              <div class="video-send-header card-header d-flex align-items-center gap-2">
                <i class="fab fa-whatsapp fa-2x" aria-hidden="true"></i>
                <h2 id="video-send-title" class="h5 mb-0">{{ t('inputs.video.send_title') }}</h2>
              </div>
              <div class="card-body">
                @if (submission(); as send) {
                  <p class="small text-muted mb-1">{{ t('inputs.video.your_code') }}</p>
                  <div class="d-flex align-items-center gap-2 mb-3">
                    <span class="video-code" [attr.aria-label]="t('inputs.video.your_code') + ' ' + send.code">{{ send.code }}</span>
                    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="copyCode()">
                      <i class="fa-regular me-1" [class.fa-copy]="!copied()" [class.fa-check]="copied()" aria-hidden="true"></i>{{ t(copied() ? 'inputs.video.copied' : 'inputs.video.copy') }}
                    </button>
                  </div>

                  <ol class="video-steps small ps-3 mb-3">
                    <li>{{ t('inputs.video.step_record') }}</li>
                    <li>{{ t('inputs.video.step_send', { account: send.account }) }}</li>
                    <li>{{ t('inputs.video.step_hand_in') }}</li>
                  </ol>

                  @if (send.link) {
                    <a class="btn btn-whatsapp w-100 mb-2" [href]="send.link" target="_blank" rel="noopener">
                      <i class="fab fa-whatsapp me-2" aria-hidden="true"></i>{{ t('inputs.video.open_whatsapp') }}
                    </a>
                    <p class="small text-muted text-center mb-3">{{ t('inputs.video.message_written', { code: send.code }) }}</p>

                    @if (send.qr) {
                      <!-- On a computer: the video is on the phone, so the phone scans this. -->
                      <div class="d-none d-md-block text-center border-top pt-3">
                        <img class="video-qr" [src]="send.qr" [alt]="t('inputs.video.qr_alt')" width="160" height="160" />
                        <p class="small text-muted mt-2 mb-0">{{ t('inputs.video.scan') }}</p>
                      </div>
                    }

                    <p class="small text-muted mt-3 mb-0">
                      {{ t('inputs.video.number', { account: send.account }) }} <span class="text-nowrap fw-semibold">{{ send.whatsapp }}</span>
                    </p>
                  } @else {
                    <div class="alert alert-warning small mb-0" role="alert">{{ t('inputs.video.no_number') }}</div>
                  }
                }
              </div>
            </section>

            <div class="card">
              <div class="card-body">
                <p class="mb-2">
                  <span class="text-muted">{{ t('inputs.your_work') }}:</span>
                  @if (myState(); as state) {
                    <span class="badge badge-pill ms-1" [class]="state.badgeClasses">
                      <i [class]="state.iconClasses" aria-hidden="true"></i>
                      {{ t(state.translationKey) }}
                    </span>
                  }
                </p>

                @if (handedIn()) {
                  <p class="mb-0">{{ t('inputs.video.handed_in') }}</p>
                } @else {
                  <button type="button" class="btn btn-primary w-100" [disabled]="handingIn()" (click)="handIn()">
                    {{ t('inputs.video.sent_it') }}
                  </button>
                  <p class="small text-muted mt-2 mb-0">{{ t('inputs.video.sent_it_help') }}</p>
                }

                @if (actionFailure(); as failed) {
                  <div class="alert alert-danger mt-3 mb-0" role="alert">{{ t(failed.translationKey) }}</div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  }
</div>
